import axios from "axios";
import AgoraRTC from "agora-rtc-sdk-ng";

const DEFAULT_SAMPLES = 2;
const DEFAULT_TIMEOUT_MS = 10000;
// Same threshold the call uses to warn about an unstable connection.
const UNSTABLE_QUALITY = 5;
const UNKNOWN_QUALITY = 0;

const fetchRtcToken = async (channel, uid) => {
  const url = `${process.env.REACT_APP_AGORA_TOKEN_SERVICE}/rtc/${channel}/subscriber/uid/${uid}`;
  const { data } = await axios.get(url);
  return data.rtcToken;
};

// Joins a throwaway Agora channel with its own client (never the shared call
// client) and collects the SDK's network-quality readings, one every ~2 s.
export const runPrecallTest = async ({
  samples = DEFAULT_SAMPLES,
  timeoutMs = DEFAULT_TIMEOUT_MS,
  signal,
} = {}) => {
  const uid = Math.floor(Math.random() * 1e9) + 1;
  const channel = `precall-test-${uid}`;
  const token = await fetchRtcToken(channel, uid);
  if (signal?.aborted) return [];
  const client = AgoraRTC.createClient({ mode: "rtc", codec: "vp8" });
  const collected = [];
  let onEnough;
  const enough = new Promise((resolve) => {
    onEnough = resolve;
  });

  client.on("network-quality", (stats) => {
    collected.push(stats);
    if (collected.length >= samples) onEnough();
  });

  signal?.addEventListener("abort", () => onEnough());

  let timer;
  try {
    await client.join(process.env.REACT_APP_AGORA_APP_ID, channel, token, uid);
    await Promise.race([
      enough,
      new Promise((resolve) => {
        timer = setTimeout(resolve, timeoutMs);
      }),
    ]);
  } finally {
    clearTimeout(timer);
    await client.leave().catch(() => {});
  }
  return collected;
};

// Being able to join already proves the Agora edge is reachable, so readings
// that are unknown (0) are ignored.
export const evaluateNetworkQuality = (readings) =>
  readings.every(({ uplinkNetworkQuality, downlinkNetworkQuality }) =>
    [uplinkNetworkQuality, downlinkNetworkQuality]
      .filter((value) => value !== UNKNOWN_QUALITY)
      .every((value) => value < UNSTABLE_QUALITY)
  );
